import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Flex,
  Link,
  Stack,
  useColorModeValue,
  useToast,
  Text,
  Button,
  HStack,
  IconButton,
  InputGroup,
  InputLeftElement,
  Input,
  Select,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import { useEnsureAuthenticated } from "@/contexts/ensureAuthenticated";
import { userPermissions } from "@/contexts/userPermissions";
import { PermissionEnum } from "@/utils/permissionEnum";
import { CiReceipt, CiSearch } from "react-icons/ci";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import { getAllProducts, updateProduct} from "@/services/consignado-product.service";
import { ConsignadoProduct } from "@/models/consignado-product.model";
import { FaUserPlus } from "react-icons/fa";

export default function ConsignadoProductTable() {

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { user } = useEnsureAuthenticated();
  const permissions = userPermissions(user);
  const toast = useToast();
  const backgroundStyle = useConditionalBackground();

  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [search, setSearch] = useState("");

  const handleSort = async (field: string) => {
    setSortBy(field);
    setSortDirection(
      sortBy === field && sortDirection === "asc" ? "desc" : "asc"
    );
  };

  const fetchProdutos = async () => await getAllProducts();

  const staleSeconds = 20;

  const { data, refetch } = useQuery({
    queryKey: ["produtos"],
    queryFn: fetchProdutos,
    staleTime: staleSeconds * 1000,
  });

  const productsData = data || [];
  const totalPages =  1;

  const headerColor = useColorModeValue("white", "white");
  const rowHoverBg = useColorModeValue("teal.50", "teal.900");
  const rowBorderColor = useColorModeValue("gray.200", "gray.600");


  const handleDeactivateProduct = async (product: ConsignadoProduct) => {
    try {
      if (!product.id) {
        throw new Error("ID do produto é indefinido.");
      }

      await updateProduct(product.id, { habilitado: false });

      toast({
        title: "Produto desativado com sucesso!",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

       await refetch();
      
    } catch (error) {
      console.error("Erro ao desativar produto:", error);
      toast({
        title: "Erro ao desativar produto",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // const handleDownload = async () => {
  //   // const exportData = productsData.map(
  //   //   (products: {
  //   //     id?: string;
  //   //     name: string;
  //   //     type: ProductType;
  //   //     percentage: number;
  //   //     partnerPercentage: number;
  //   //     minLimit?: number | null;
  //   //     maxLimit?: number | null;
  //   //     enabled: boolean;
  //   //   }) => ({
  //   //     id: products.id,
  //   //     name: products.name,
  //   //     type: products.type,
  //   //     percetage: products.percentage,
  //   //     partnerPercentage: products.partnerPercentage,
  //   //     minLimit: products.minLimit,
  //   //     maxLimit: products.maxLimit,
  //   //     enabled: products.enabled,
  //   //   })
  //   // );

  //   // const worksheet = XLSX.utils.json_to_sheet(exportData);
  //   // const workbook = XLSX.utils.book_new();
  //   // XLSX.utils.book_append_sheet(workbook, worksheet, "Users");

  //   // const excelBuffer = XLSX.write(workbook, {
  //   //   bookType: "xlsx",
  //   //   type: "array",
  //   // });

  //   // const dataBlob = new Blob([excelBuffer], {
  //   //   type: "application/octet-stream",
  //   // });

  //   // const url = window.URL.createObjectURL(dataBlob);
  //   // const link = document.createElement("a");
  //   // link.href = url;
  //   // link.download = "products.xlsx";
  //   // document.body.appendChild(link);
  //   // link.click();
  //   // document.body.removeChild(link);

  //   // toast({
  //   //   title: "Arquivo Excel de produtos baixado com sucesso.",
  //   //   status: "success",
  //   //   duration: 3000,
  //   //   isClosable: true,
  //   // });
  // };

  const filteredProducts = useMemo(() => {
    if (!search) return productsData;
    const lowerSearch = search.toLowerCase();

    return productsData.filter(
      (product: ConsignadoProduct) => {
        const matchesName = product.nome?.toLowerCase().includes(lowerSearch);
        const matchesId = product.id
          ?.toString()
          .toLowerCase()
          .includes(lowerSearch);
        // const matchesType = product.type?.toLowerCase().includes(lowerSearch);
        // const matchesPercentage = product.percentage
        //   ?.toString()
        //   .toLowerCase()
        //   .includes(lowerSearch);
        // const matchesMaxLimit = product.maxLimit
        //   ?.toString()
        //   .toLowerCase()
        //   .includes(lowerSearch);
        // const matchesMinLimit = product.minLimit
        //   ?.toString()
        //   .toLowerCase()
        //   .includes(lowerSearch);
        // const matchesStatus = (product.enabled ? "ativo" : "inativo")
        //   .toLowerCase()
        //   .includes(lowerSearch);

        return (
          matchesName ||
          matchesId //||
          // matchesType ||
          // matchesPercentage ||
          // matchesMaxLimit ||
          // matchesMinLimit ||
          // matchesStatus
        );
      }
    );
  }, [productsData, search]);

  return (
    <Box
      padding={{ base: "2", md: "4" }}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Box
        {...backgroundStyle}
        borderRadius="md"
        p={{ base: 4, md: 6 }}
        mb={4}
        color="white"
        boxShadow="md"
        width="100%"
      >
        <Flex
          alignItems={{ base: "center", md: "center" }}
          justifyContent="space-between"
          flexDirection={{ base: "column", md: "row" }}
          textAlign={{ base: "center", md: "left" }}
        >
          <Flex alignItems="center" mb={{ base: 4, md: 0 }}>
            <CiReceipt size="2rem" />
            <Text
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              ml={4}
              fontFamily="Lexend Deca"
            >
              Produtos Consignado CLT
            </Text>
          </Flex>

          <Flex
            flexDirection={{ base: "column", md: "row" }}
            alignItems="flex-end"
            mt={{ base: 4, md: 0 }}
            width="100%"
            justifyContent={{ base: "center", md: "flex-end" }}
            gap={4}
          >
            {permissions.some(
              (permission) => permission === PermissionEnum.CREATE_USER
            ) && (
                <Stack
                  direction={{ base: "column", md: "row" }}
                  spacing={4}
                  width="100%"
                  justifyContent={{ base: "center", md: "end" }}
                >
                  <Link
                    as={RouterLink}
                    to="novo"
                    bg="white"
                    color="black"
                    px={4}
                    py={{ base: 4, md: 2 }}
                    borderRadius="md"
                    fontWeight="bold"
                    _hover={{ bg: "gray.200" }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize={{ base: "lg", md: "md" }}
                    height={{ base: "56px", md: "40px" }}
                    width={{ base: "100%", md: "auto" }}
                    fontFamily="Lexend Deca"
                  >
                    <FaUserPlus
                      style={{ marginRight: "8px", fontSize: "20px" }}
                    />
                    Cadastrar Produto
                  </Link>
                </Stack>
              )}
            {/* <HStack spacing={2} align={"flex-start"}>
              <IconButton
                icon={<FaFileExcel />}
                aria-label="Download Excel"
                onClick={handleDownload}
                colorScheme="green"
              />
            </HStack> */}
          </Flex>
        </Flex>
        <Flex w="30%" mt={8} gap={2}>
          <InputGroup>
            <InputLeftElement mt={1} pointerEvents="none">
              <CiSearch color="black" size="2rem" />
            </InputLeftElement>
            <Input
              height={{ base: "56px", md: "48px" }}
              bg={"white"}
              placeholder="Pesquisar..."
              variant="filled"
              type="text"
              maxLength={30}
              color="black"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              _focus={{ bg: "white" }}
              _hover={{ bg: "white" }}
              fontFamily="Lexend Deca"
            />
          </InputGroup>
        </Flex>
      </Box>
      <Flex
        justify="center"
        alignItems="center"
        mb={4}
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
      >
        <Flex alignItems="center" mb={{ base: 2, md: 0 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mt={{ base: 2, md: 0 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>
      <Box width="100%" overflow="visible">
        <TableContainer borderRadius="md" boxShadow="md" width="100%">
          <Table size="sm">
            <Thead
              {...backgroundStyle}
              position="sticky"
              top={0}
              zIndex={1}
              boxShadow="md"
            >
              <Tr>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>AÇÕES</Text>
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Id</Text>
                    <IconButton
                      icon={
                        sortBy === "id" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Id"
                      bg={"gray.200"}
                      onClick={() => handleSort("id")}
                    />
                  </HStack>
                </Th>
                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>NOME</Text>
                    <IconButton
                      icon={
                        sortBy === "name" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Name"
                      bg={"gray.200"}
                      onClick={() => handleSort("name")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Parcelas</Text>
                    <IconButton
                      icon={
                        sortBy === "percentage" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Percentage"
                      bg={"gray.200"}
                      onClick={() => handleSort("percentage")}
                    />
                  </HStack>
                </Th>
                {/* <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Seguro</Text>
                    <IconButton
                      icon={
                        sortBy === "partnerPercentage" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Comissão"
                      bg={"gray.200"}
                      onClick={() => handleSort("partnerPercentage")}
                    />
                  </HStack>
                </Th> */}

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>Juros (a.m.)</Text>
                    <IconButton
                      icon={
                        sortBy === "partnerPercentage" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by Comissão"
                      bg={"gray.200"}
                      onClick={() => handleSort("partnerPercentage")}
                    />
                  </HStack>
                </Th>

                <Th color={headerColor} textAlign="center" fontSize="xs" py={5}>
                  <HStack justify="center">
                    <Text>ATIVO</Text>
                    <IconButton
                      icon={
                        sortBy === "enabled" && sortDirection === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      variant="ghost"
                      size="1"
                      fontSize={"1rem"}
                      borderRadius={"lg"}
                      aria-label="Sort by enabled"
                      bg={"gray.200"}
                      onClick={() => handleSort("enabled")}
                    />
                  </HStack>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {filteredProducts.map((product: ConsignadoProduct, index: number) => (
                <Tr
                  key={index}
                  _hover={{ bg: rowHoverBg }}
                  transition="all 0.3s ease-in-out"
                  borderBottom="1px solid"
                  borderColor={rowBorderColor}
                  fontFamily="Lexend Deca"

                >
                  <Td
                    textAlign="center"
                    fontSize={{ base: "lg", md: "sm" }}
                    py={{ base: 2, md: 3 }}
                    fontFamily="Lexend Deca"
                    width={"250px"}
                  >
                    <Flex justify="center" flexDirection="column" gap={2}>

                      <Button
                        size={{ base: "md", md: "md" }}
                        colorScheme="red"
                        onClick={() => handleDeactivateProduct(product)}
                        width="100%"
                        py={{ base: 2.5, md: 2 }}
                        fontSize={{ base: "md", md: "sm" }}
                        fontWeight="bold"
                        fontFamily="Lexend Deca"
                      >
                        Desativar Produto
                      </Button>
                    </Flex>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    <Link
                      as={RouterLink}
                      to={`/consignado/produtos/${product.id}`}
                      color="blue.500"
                    >
                      {product.id}
                    </Link>
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    {product.nome}
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    {product.numero_parcelas}
                  </Td>

                  {/* <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    {(product.porcentual_seguro!)?.toLocaleString("pt-BR", {
                      style: "percent"
                    })}
                  </Td> */}

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    {(product.taxa_mensal!)?.toLocaleString("pt-BR", {
                      style: "percent"
                    })}
                  </Td>

                  <Td textAlign="center" fontSize="sm" py={{ base: 2, md: 3 }}>
                    {product.habilitado ? "Ativo" : "Inativo"}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>

      <Flex
        justify="center"
        alignItems="center"
        flexDirection={{ base: "column", md: "row" }}
        width="100%"
        maxWidth="1200px"
        px={{ base: 2, md: 0 }}
      >
        <Flex alignItems="center" mt={4} mb={{ base: 2, md: 2 }}>
          <IconButton
            icon={<ChevronLeftIcon />}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            isDisabled={page === 1}
            mr={2}
            aria-label="Previous Page"
            size={{ base: "md", md: "sm" }}
          />
          <Text fontSize={{ base: "lg", md: "md" }} fontFamily="Lexend Deca">
            Página {page} de {totalPages}
          </Text>
          <IconButton
            icon={<ChevronRightIcon />}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            isDisabled={page === totalPages}
            ml={2}
            aria-label="Next Page"
            size={{ base: "md", md: "sm" }}
          />
        </Flex>
        <Box ml={{ base: 0, md: 4 }} mb={2} mt={{ base: 2, md: 4 }}>
          <Select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            width={{ base: "100%", md: "auto" }}
            size={{ base: "md", md: "sm" }}
            fontSize={{ base: "lg", md: "md" }}
            fontFamily="Lexend Deca"
          >
            <option value={10}>10 por página</option>
            <option value={20}>20 por página</option>
            <option value={50}>50 por página</option>
            <option value={100}>100 por página</option>
          </Select>
        </Box>
      </Flex>
    </Box>
  );
}
